import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { GatheringFormDialog } from '@/components/programs/gathering-form-dialog';
import {
  DayDetailDatesCell,
  DayDetailReadingsCell,
} from '@/components/programs/day-detail-table-cells';
import { ProgramDayDetailDialog } from '@/components/programs/program-day-detail-dialog';
import { programTimeRangeLabel } from '@/components/programs/program-time';
import { ProgramRegistrationsSection } from '@/components/programs/program-registrations-section';
import type { ProgramDayDetail } from '@/components/programs/program-day-detail';
import {
  type Gathering,
  type GatheringKind,
  detailPathFor,
  listPathForKind,
  parseServicePattern,
  choosableDayDetailDates,
  scheduleDateCountForGathering,
  scheduleSummaryDatesForGathering,
  scheduleText,
  upcomingGatheringDates,
} from '@/components/programs/gathering-schedule';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { ImageLightbox } from '@/components/shared/image-lightbox';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { churchToday, formatDate, formatDateTime } from '@/utils/format';

const dayDetailReadings = (detail: ProgramDayDetail): string[] =>
  detail.scriptureRefs?.length
    ? detail.scriptureRefs
    : detail.scriptureRef
      ? [detail.scriptureRef]
      : [];

interface UpcomingDate {
  date: string;
  startTime: string;
  endTime: string;
}

export const GatheringDetailPage = ({ kind }: { kind: GatheringKind }) => {
  const { programId = '' } = useParams();
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const ns = kind === 'service' ? 'services' : 'programs';
  const listPath = listPathForKind(kind);
  const canDeletePermission = hasPermission(membership, PERMISSIONS.PROGRAMS_DELETE);
  const canEdit = hasPermission(membership, PERMISSIONS.PROGRAMS_EDIT);

  const [editOpen, setEditOpen] = useState(false);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [editingDayDetail, setEditingDayDetail] = useState<ProgramDayDetail | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const overviewCardRef = useRef<HTMLDivElement>(null);
  const [overviewHeight, setOverviewHeight] = useState<number>();

  const gathering = useQuery({
    queryKey: ['programs', programId],
    queryFn: () => api.get<Gathering>(`/programs/${programId}`),
    enabled: Boolean(programId),
  });

  const dayDetails = useQuery({
    queryKey: ['programs', programId, 'day-details'],
    queryFn: () =>
      api.get<{ data: ProgramDayDetail[] }>(`/programs/${programId}/day-details`),
    enabled: Boolean(programId),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/programs/${programId}`),
    onSuccess: () => {
      toast.success(t(`detail.deleted.${kind}`));
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      void navigate(listPath);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: () => api.post<Gathering>(`/programs/${programId}/publish`),
    onSuccess: () => {
      toast.success(t(`${ns}.published`));
      setPublishOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['programs', programId] });
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: () => api.post<Gathering>(`/programs/${programId}/unpublish`),
    onSuccess: () => {
      toast.success(t(`${ns}.unpublished`));
      setUnpublishOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['programs', programId] });
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  useEffect(() => {
    const row = gathering.data;
    if (!row) return;
    if (row.kind !== kind) {
      void navigate(detailPathFor(row.kind, row.id), { replace: true });
    }
  }, [gathering.data, kind, navigate]);

  useEffect(() => {
    const element = overviewCardRef.current;
    const gatheringRow = gathering.data;
    if (!element || !gatheringRow?.coverImageUrl) {
      setOverviewHeight(undefined);
      return;
    }

    const syncHeight = () => {
      setOverviewHeight(element.getBoundingClientRect().height);
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [gathering.data?.coverImageUrl, gathering.data?.id]);

  const today = churchToday();
  const row = gathering.data;
  const scheduleDateCount = useMemo(
    () => (row ? scheduleDateCountForGathering(row) : null),
    [row],
  );
  const scheduleDates = useMemo(
    () => (row ? scheduleSummaryDatesForGathering(row) : []),
    [row],
  );
  const choosableDates = useMemo(
    () => (row ? choosableDayDetailDates(row, today) : []),
    [row, today],
  );
  const daysWithDetailsCount = useMemo(
    () =>
      (dayDetails.data?.data ?? []).reduce((sum, item) => sum + item.dates.length, 0),
    [dayDetails.data?.data],
  );
  const upcoming = useMemo(
    () => (row ? upcomingGatheringDates(row, today, 5) : []),
    [row, today],
  );

  const takenDates = useMemo(() => {
    const rows = dayDetails.data?.data ?? [];
    if (editingDayDetail) {
      return rows
        .filter((item) => item.id !== editingDayDetail.id)
        .flatMap((item) => item.dates);
    }
    return rows.flatMap((item) => item.dates);
  }, [dayDetails.data?.data, editingDayDetail]);

  const dayDetailRows = useMemo(() => {
    const rows = [...(dayDetails.data?.data ?? [])];
    rows.sort((a, b) => {
      const bySort = b.sortOrder - a.sortOrder;
      if (bySort !== 0) return bySort;
      return b.createdAt.localeCompare(a.createdAt);
    });
    return rows;
  }, [dayDetails.data?.data]);

  const lightboxItems = useMemo(() => {
    if (!row) return [];
    const items: { url: string; caption: string | null }[] = [];
    if (row.coverImageUrl) {
      items.push({ url: row.coverImageUrl, caption: row.name });
    }
    for (const detail of dayDetailRows) {
      if (detail.imageUrl) {
        const datesLabel =
          detail.dates.length > 0
            ? detail.dates.map((date) => formatDate(date)).join(' · ')
            : null;
        items.push({
          url: detail.imageUrl,
          caption: [datesLabel, detail.theme].filter(Boolean).join(' · ') || null,
        });
      }
    }
    return items;
  }, [row, dayDetailRows]);

  const dayDetailImageIndexById = useMemo(() => {
    const map = new Map<string, number>();
    let index = row?.coverImageUrl ? 1 : 0;
    for (const detail of dayDetailRows) {
      if (detail.imageUrl) {
        map.set(detail.id, index);
        index += 1;
      }
    }
    return map;
  }, [row?.coverImageUrl, dayDetailRows]);

  if (!row) {
    return (
      <div>
        <PageHeader
          title={t(`${ns}.details`)}
          breadcrumbs={[
            { label: t(`${ns}.title`), to: listPath },
            { label: tCommon('state.loading') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  const isOneOff = row.scheduleType === 'one_off';
  const isCustom = row.scheduleType === 'custom';
  const parsed = parseServicePattern(row);
  const canManageDayDetails =
    kind === 'program'
      ? Boolean(row.startsOn) || row.scheduleType === 'custom' || scheduleDates.length > 0
      : row.scheduleType === 'one_off'
        ? Boolean(row.startsOn)
        : Boolean(row.scheduleRule);
  const dayDetailsKind = kind === 'service' ? 'service' : 'program';

  const dayDetailColumns: DataTableColumn<ProgramDayDetail>[] = [
    {
      id: 'dates',
      header: t('dayDetails.columns.dates'),
      width: '8.5rem',
      cell: (detail) => <DayDetailDatesCell dates={detail.dates} />,
    },
    {
      id: 'image',
      header: t('dayDetails.columns.image'),
      width: '4.5rem',
      align: 'center',
      cell: (detail) => {
        if (!detail.imageUrl) return '—';
        const imageIndex = dayDetailImageIndexById.get(detail.id);
        return (
          <img
            src={detail.imageUrl}
            alt={t('dayDetails.previewImage')}
            className="mx-auto size-12 cursor-pointer rounded-md object-cover transition-opacity hover:opacity-90"
            onClick={(event) => {
              event.stopPropagation();
              if (imageIndex !== undefined) setLightboxIndex(imageIndex);
            }}
          />
        );
      },
    },
    {
      id: 'theme',
      header: t('dayDetails.columns.theme'),
      cell: (detail) => detail.theme || '—',
    },
    {
      id: 'speaker',
      header: t('dayDetails.columns.speaker'),
      width: '11rem',
      cell: (detail) => (
        <span className="whitespace-nowrap">
          {detail.speakerMemberName ?? detail.guestSpeaker ?? '—'}
        </span>
      ),
    },
    {
      id: 'readings',
      header: t('dayDetails.columns.readings'),
      cell: (detail) => <DayDetailReadingsCell readings={dayDetailReadings(detail)} />,
      hideBelowMd: true,
    },
    {
      id: 'time',
      header: t('dayDetails.columns.time'),
      width: '8.5rem',
      cell: (detail) => (
        <span className="whitespace-nowrap">
          {detail.startTime && detail.endTime
            ? programTimeRangeLabel(detail.startTime, detail.endTime, t)
            : '—'}
        </span>
      ),
      hideBelowMd: true,
    },
  ];

  const dayDetailsCard = (
    <Card className={kind === 'program' && !isOneOff ? undefined : 'mt-0'}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>{t('dayDetails.title')}</CardTitle>
          <CardDescription>{t(`dayDetails.sectionDescription.${dayDetailsKind}`)}</CardDescription>
        </div>
        {canEdit && canManageDayDetails ? (
          <Button
            size="sm"
            onClick={() => {
              setEditingDayDetail(null);
              setDayDetailOpen(true);
            }}
          >
            {t('dayDetails.add')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {!canManageDayDetails ? (
          <p className="text-body-sm text-fg-secondary">
            {t(`dayDetails.awaitingDates.${dayDetailsKind}`)}
          </p>
        ) : (
          <DataTable
            className="[&_table]:table-fixed"
            rows={dayDetailRows}
            columns={dayDetailColumns}
            rowKey={(detail) => detail.id}
            loading={dayDetails.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('dayDetails.empty')}</p>}
            onRowClick={
              canEdit
                ? (detail) => {
                    setEditingDayDetail(detail);
                    setDayDetailOpen(true);
                  }
                : undefined
            }
          />
        )}
      </CardContent>
    </Card>
  );

  return (
    <div>
      <PageHeader
        title={row.name}
        breadcrumbs={[
          { label: t(`${ns}.title`), to: listPath },
          { label: row.name },
        ]}
        meta={
          <>
            <Badge variant={row.isActive ? 'success' : 'neutral'}>
              {row.isActive ? t('detail.published') : t('detail.draft')}
            </Badge>
            <span className="text-body-sm text-fg-secondary">{scheduleText(row, t)}</span>
          </>
        }
        headerActions={
          <div className="flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/calendar">{t('calendar.openCalendar')}</Link>
            </Button>
            {canEdit ? (
              row.isActive ? (
                <Button size="sm" variant="secondary" onClick={() => setUnpublishOpen(true)}>
                  {t('unpublish')}
                </Button>
              ) : (
                <Button size="sm" onClick={() => setPublishOpen(true)}>
                  {t('publish')}
                </Button>
              )
            ) : null}
            {canDeletePermission ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (row.canDelete === false) {
                    toast.error(tErrors('PROGRAM_HAS_LINKED_RECORDS'));
                    return;
                  }
                  setDeleteOpen(true);
                }}
              >
                {t('detail.delete')}
              </Button>
            ) : null}
            <Button size="sm" onClick={() => setEditOpen(true)}>
              {tCommon('action.edit')}
            </Button>
          </div>
        }
      />

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card ref={overviewCardRef}>
          <CardHeader>
            <CardTitle>{t('detail.overview')}</CardTitle>
            <CardDescription>{t(`detail.overviewDescription.${kind}`)}</CardDescription>
          </CardHeader>
          <CardContent>
            {row.description ? (
              <p className="mb-4 text-body-sm text-fg">{row.description}</p>
            ) : null}
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-caption text-fg-secondary">{t('detail.fields.kind')}</dt>
                <dd className="text-body-sm text-fg">{t(`detail.kind.${row.kind}`)}</dd>
              </div>
              <div>
                <dt className="text-caption text-fg-secondary">{t(`${ns}.detailSchedule`)}</dt>
                <dd className="text-body-sm text-fg">{scheduleText(row, t)}</dd>
              </div>
              {!isOneOff && !isCustom ? (
                <div>
                  <dt className="text-caption text-fg-secondary">{t(`${ns}.pattern`)}</dt>
                  <dd className="text-body-sm text-fg">
                    {t(`${ns}.patterns.${parsed.pattern}`)}
                  </dd>
                </div>
              ) : null}
              {!isOneOff && !isCustom ? (
                <div>
                  <dt className="text-caption text-fg-secondary">{t('services.weekday')}</dt>
                  <dd className="text-body-sm text-fg">{t(`weekdays.${parsed.weekday}`)}</dd>
                </div>
              ) : null}
              {isCustom ? (
                <div className="sm:col-span-2">
                  <dt className="text-caption text-fg-secondary">{t('programs.specificDays')}</dt>
                  <dd className="text-body-sm text-fg">
                    {parsed.specificDates.map((date) => formatDate(date)).join(', ')}
                  </dd>
                </div>
              ) : null}
              {isOneOff ? (
                <div>
                  <dt className="text-caption text-fg-secondary">
                    {kind === 'service' ? t('services.specialDate') : t('programs.startsOn')}
                  </dt>
                  <dd className="text-body-sm text-fg">
                    {row.startsOn ? formatDate(row.startsOn) : '—'}
                  </dd>
                </div>
              ) : null}
              {kind === 'program' && isOneOff && row.endsOn && row.endsOn !== row.startsOn ? (
                <div>
                  <dt className="text-caption text-fg-secondary">{t('programs.endsOn')}</dt>
                  <dd className="text-body-sm text-fg">{formatDate(row.endsOn)}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-caption text-fg-secondary">{t(`${ns}.detailTime`)}</dt>
                <dd className="text-body-sm text-fg">
                  {row.startTime && row.endTime
                    ? programTimeRangeLabel(row.startTime, row.endTime, t)
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-caption text-fg-secondary">{t(`${ns}.detailVenue`)}</dt>
                <dd className="text-body-sm text-fg">{row.venueName ?? '—'}</dd>
              </div>
              {row.joinUrl ? (
                <div className="sm:col-span-2">
                  <dt className="text-caption text-fg-secondary">{t('joinOnline')}</dt>
                  <dd className="text-body-sm text-fg">
                    <a
                      href={row.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all font-medium text-brand hover:underline"
                    >
                      {row.joinUrl}
                    </a>
                  </dd>
                </div>
              ) : null}
              {row.joinRequiresCode && row.joinCode ? (
                <div>
                  <dt className="text-caption text-fg-secondary">{t('joinCode')}</dt>
                  <dd className="text-body-sm text-fg">{row.joinCode}</dd>
                </div>
              ) : null}
            </dl>
          </CardContent>
        </Card>

        <div
          className={row.coverImageUrl ? 'relative min-h-0 overflow-hidden' : undefined}
          style={row.coverImageUrl ? { height: overviewHeight ?? 0 } : undefined}
        >
          <Card
            className={
              row.coverImageUrl ? 'absolute inset-0 flex flex-col overflow-hidden' : undefined
            }
          >
            <CardHeader className="shrink-0">
              <CardTitle>{t('detail.record')}</CardTitle>
              <CardDescription>{t('detail.recordDescription')}</CardDescription>
            </CardHeader>
            <CardContent
              className={
                row.coverImageUrl
                  ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-hidden text-body-sm text-fg-secondary'
                  : 'space-y-3 text-body-sm text-fg-secondary'
              }
            >
              <div className="shrink-0 space-y-3">
                <p>
                  {t('detail.createdAt', {
                    when: formatDateTime(row.createdAt),
                  })}
                </p>
                <p>
                  {t('detail.updatedAt', {
                    when: formatDateTime(row.updatedAt),
                  })}
                </p>
                {kind === 'service' || (kind === 'program' && isOneOff) ? (
                  <p>{t('detail.daysWithDetails', { count: daysWithDetailsCount })}</p>
                ) : scheduleDateCount !== null ? (
                  <p>{t('detail.dateCount', { count: scheduleDateCount })}</p>
                ) : null}
              </div>
              {row.coverImageUrl ? (
                <button
                  type="button"
                  className="block min-h-0 w-full flex-1 overflow-hidden rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('detail.previewCover', { name: row.name })}
                  onClick={() => setLightboxIndex(0)}
                >
                  <img
                    src={row.coverImageUrl}
                    alt=""
                    className="size-full rounded-xl object-cover object-top transition-opacity hover:opacity-90"
                  />
                </button>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {kind === 'program' ? (
        <Tabs defaultValue="day-details">
          <TabsList>
            <TabsTrigger value="day-details">{t('detail.tabs.dayDetails')}</TabsTrigger>
            {row.registrationMode !== 'none' ? (
              <TabsTrigger value="registrations">{t('detail.tabs.registrations')}</TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="day-details" className="space-y-4">
            {dayDetailsCard}
            {!isOneOff ? (
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                  <div>
                    <CardTitle>{t('calendar.nextDates')}</CardTitle>
                    <Tip tipId="programs.nextDates" />
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <Link to="/calendar">{t('calendar.openCalendar')}</Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  {upcoming.length === 0 ? (
                    <p className="text-body-sm text-fg-secondary">{t('calendar.nextDatesEmpty')}</p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {upcoming.map((occ: UpcomingDate) => (
                        <li
                          key={occ.date}
                          className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-body-sm"
                        >
                          <span className="font-medium text-fg">{formatDate(occ.date)}</span>
                          <span className="text-fg-secondary">
                            {t('timeRange', {
                              start: occ.startTime,
                              end: occ.endTime,
                            })}
                            {row.venueName ? ` · ${row.venueName}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </TabsContent>
          {row.registrationMode !== 'none' ? (
            <TabsContent value="registrations">
              <ProgramRegistrationsSection
                programId={row.id}
                registrationMode={row.registrationMode ?? 'registration'}
                registrationSettings={{
                  registrationAccess: row.registrationAccess,
                  registrationFeeType: row.registrationFeeType,
                  registrationFeeAmountMinor: row.registrationFeeAmountMinor,
                  registrationFeeCurrency: row.registrationFeeCurrency,
                  registrationAllowedPaymentMethods: row.registrationAllowedPaymentMethods,
                  capacity: row.capacity,
                }}
              />
            </TabsContent>
          ) : null}
        </Tabs>
      ) : (
        dayDetailsCard
      )}

      {canManageDayDetails ? (
        <ProgramDayDetailDialog
          open={dayDetailOpen}
          onOpenChange={setDayDetailOpen}
          programId={row.id}
          scheduleType={row.scheduleType}
          startsOn={row.startsOn}
          endsOn={row.endsOn}
          occurrenceDates={choosableDates}
          defaultStartTime={row.startTime}
          defaultEndTime={row.endTime}
          defaultVenueId={row.venueId}
          takenDates={takenDates}
          detail={editingDayDetail}
          enableWorship={kind === 'service'}
        />
      ) : null}

      <GatheringFormDialog
        kind={kind}
        open={editOpen}
        onOpenChange={setEditOpen}
        gathering={row}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t(`detail.confirmDelete.title.${kind}`)}
        description={t(`detail.confirmDelete.description.${kind}`)}
        confirmLabel={t(`detail.confirmDelete.confirm.${kind}`)}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />

      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={t(`confirm.publish.title.${kind}`, { name: row.name })}
        description={t(`confirm.publish.description.${kind}`)}
        confirmLabel={t(`confirm.publish.confirm.${kind}`)}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => publish.mutate()}
      />

      <ConfirmDialog
        open={unpublishOpen}
        onOpenChange={setUnpublishOpen}
        title={t(`confirm.unpublish.title.${kind}`, { name: row.name })}
        description={t(`confirm.unpublish.description.${kind}`)}
        confirmLabel={t(`confirm.unpublish.confirm.${kind}`)}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => unpublish.mutate()}
      />

      <ImageLightbox
        images={lightboxItems.map((item) => item.url)}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        caption={
          lightboxIndex !== null && lightboxIndex >= 0 && lightboxIndex < lightboxItems.length
            ? lightboxItems[lightboxIndex]?.caption
            : null
        }
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count:
            lightboxIndex !== null
              ? tCommon('lightbox.imageCount', {
                  current: lightboxIndex + 1,
                  total: lightboxItems.length,
                })
              : '',
          imageAlt: tCommon('lightbox.title'),
        }}
      />
    </div>
  );
};
